import React from 'react';
import {
  InfoCard,
  InfoCardVariants,
  Table,
  TableColumn,
  Link,
} from '@backstage/core-components';
import { useEntity, useRelatedEntities } from '@backstage/plugin-catalog-react';
import { RELATION_HAS_PART, Entity } from '@backstage/catalog-model';
import { Chip, Typography } from '@material-ui/core';

interface ComponentRow {
  entity: Entity;
  name: string;
  namespace: string;
  type: string;
  lifecycle: string;
  description: string;
}

/**
 * Custom paginated "Has Components" card for system pages
 * Uses PhysicsX terminology (Ring instead of Lifecycle)
 */
export const PaginatedHasComponentsCard = (props: {
  variant?: InfoCardVariants;
  title?: string;
}) => {
  const { variant, title = 'Has components' } = props;
  const { entity } = useEntity();
  
  const { entities, loading, error } = useRelatedEntities(entity, {
    type: RELATION_HAS_PART,
    kind: 'Component',
  });

  const columns: TableColumn<ComponentRow>[] = [
    {
      title: 'Name',
      field: 'name',
      highlight: true,
      cellStyle: {
        whiteSpace: 'nowrap',
      },
      render: (row) => {
        const kind = row.entity.kind.toLowerCase();
        const namespace = row.namespace;
        const name = row.name;
        // Show namespace/name format for components with non-default namespace
        let displayName = row.entity.metadata.title || name;
        if (namespace !== 'default') {
          displayName = row.entity.metadata.title || `${namespace}/${name}`;
        }
        return (
          <Link to={`/catalog/${namespace}/${kind}/${name}`}>
            {displayName}
          </Link>
        );
      },
    },
    {
      title: 'Owner',
      field: 'entity.spec.owner',
      cellStyle: {
        whiteSpace: 'nowrap',
      },
      render: (row) => {
        const ownerRelation = row.entity.relations?.find(
          r => r.type === 'ownedBy'
        );
        if (!ownerRelation) return null;
        
        // Parse the entity ref (format: kind:namespace/name)
        const ref = ownerRelation.targetRef;
        const [kindPart, rest] = ref.split(':');
        const [namespace, name] = rest.split('/');
        
        return (
          <Link to={`/catalog/${namespace}/${kindPart.toLowerCase()}/${name}`}>
            {name}
          </Link>
        );
      },
    },
    {
      title: 'Type',
      field: 'type',
      cellStyle: {
        whiteSpace: 'nowrap',
      },
      render: (row) => (
        row.type ? <Chip size="small" label={row.type} /> : null
      ),
    },
    {
      title: 'Ring',
      field: 'lifecycle',
      cellStyle: {
        whiteSpace: 'nowrap',
      },
      render: (row) => (
        row.lifecycle ? <Chip size="small" label={row.lifecycle} /> : null
      ),
    },
    {
      title: 'Description',
      field: 'description',
      cellStyle: {
        maxWidth: '300px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
    },
  ];

  const data: ComponentRow[] = (entities || []).map((e) => ({
    entity: e,
    name: e.metadata.name,
    namespace: e.metadata.namespace || 'default',
    type: (e.spec as { type?: string })?.type || '',
    lifecycle: (e.spec as { lifecycle?: string })?.lifecycle || '',
    description: e.metadata.description || '',
  }));

  if (error) {
    return (
      <InfoCard title={title} variant={variant}>
        <Typography color="error">
          Failed to load components: {error.message}
        </Typography>
      </InfoCard>
    );
  }

  return (
    <InfoCard title={title} variant={variant} noPadding>
      <Table
        options={{
          search: true,
          paging: true,
          pageSize: 10,
          pageSizeOptions: [5, 10, 20, 50],
          padding: 'dense',
          emptyRowsWhenPaging: false,
          showEmptyDataSourceMessage: !loading,
        }}
        isLoading={loading}
        columns={columns}
        data={data}
        emptyContent={
          <Typography style={{ padding: '16px', textAlign: 'center' }}>
            No components found
          </Typography>
        }
      />
    </InfoCard>
  );
};
